import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

export default function StanstedDropoffCalculator() {
  const [form, setForm] = useState({
    terminal: "NORTH",
    journey_type: "DROPOFF", // DROPOFF | PICKUP
    planned_minutes: 8,
    vehicle_type: "car", // car | taxi_phv | coach_bus | motorcycle | recovery | emergency | military
    blue_badge: false,
    blue_badge_registered: false
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);
  const changeTimer = useRef(null);

  // Prefill defaults + auto-run (same style as your Train tool)
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      terminal: "NORTH",
      journey_type: "DROPOFF",
      planned_minutes: 8,
      vehicle_type: "car",
      blue_badge: false,
      blue_badge_registered: false
    }));
  }, []);

  useEffect(() => {
    if (autoSubmitted.current) return;
    // run once after initial prefill
    autoSubmitted.current = true;
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoSubmitted.current) return;
    if (changeTimer.current) clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => {
      submit();
    }, 320);
    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const submit = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiTerminal = "T5"; // backend endpoint expects this placeholder; Stansted display handled separately
      const payload = {
        terminal: apiTerminal,
        journey_type: "DROPOFF",
        vehicle_type: "car",
        blue_badge: form.blue_badge,
        blue_badge_registered: form.blue_badge_registered,
        overheight: false,
        planned_minutes: clampMinutes(form.planned_minutes)
      };

      const res = await api.post("/api/heathrow-dropoff/calc", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate drop-off costs. Please try again.");
    }

    setLoading(false);
  };

  const money = v => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const badge = (text, tone = "muted") => (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        marginLeft: 8,
        border: "1px solid #e5e7eb",
        background: tone === "warn" ? "#fff7ed" : tone === "ok" ? "#ecfdf5" : "#f9fafb",
        color: "#111827"
      }}
    >
      {text}
    </span>
  );

  const optionLabel = (id) => {
    if (id === "DROPOFF_ZONE") return "Express Set Down (drop off)";
    if (id === "SHORT_STAY") return "Short Stay parking";
    if (id === "PARK_RIDE") return "Mid Stay";
    return id || "—";
  };

  const optionTagline = (id) => {
    if (id === "DROPOFF_ZONE") return "Fastest option (PCN risk if overstaying)";
    if (id === "SHORT_STAY") return "Best for pick-ups / waiting near terminal";
      if (id === "PARK_RIDE") return "Includes shuttle transfer to terminal (10–12 mins)";
    return "Compare by price & hassle";
  };

  const clampMinutes = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 8;
    return Math.min(10080, Math.max(1, num));
  };

  const renderMedalHero = () => {
    const palette = {
      GOLD: { bg: "#0b172a", text: "#5eead4", border: "#22d3ee", emoji: "🥇" },
      SILVER: { bg: "#1f2937", text: "#e5e7eb", border: "#cbd5e1", emoji: "🥈" },
      BRONZE: { bg: "#2b1b0f", text: "#fdba74", border: "#fb923c", emoji: "🥉" }
    };

    const options = Array.isArray(result?.options) ? result.options : [];
    const numMinutes = (() => {
      const fromResult = Number(result?.journey?.planned_minutes);
      if (Number.isFinite(fromResult)) return fromResult;
      const fromForm = Number(form.planned_minutes);
      if (Number.isFinite(fromForm)) return fromForm;
      return 0;
    })();

    const byId = options.reduce((acc, opt) => ({ ...acc, [opt.id]: opt }), {});
    const isExempt = false; // Stansted Blue Badge not exempt for forecourt in this flow

    const baseOption = (id) => {
      const raw = byId[id];
      if (!raw) return null;
      let warnings = Array.isArray(raw.warnings) ? [...raw.warnings] : [];
      const infoNotes = [];

      if (id === "DROPOFF_ZONE") {
        warnings = [];
        const mins = Math.max(0, numMinutes);
        const expressCost = mins <= 15 ? 7 : mins <= 30 ? 25 : 25;
        raw.cost = mins > 30 ? null : expressCost;
        if (mins > 30) {
          warnings.unshift("Not suitable for stays over 30 minutes. PCN risk if exceeded (reduced to £60 if paid within 14 days).");
          warnings.unshift("Maximum stay is 30 minutes.");
        } else {
          warnings.unshift("Maximum stay 30 minutes. Vehicles unattended may be towed; towing fee applies.");
          warnings.unshift("£20 charge for re-entering within 30 minutes of first entry.");
          warnings.unshift("Pay online; payment required by midnight the same day.");
          warnings.unshift("Express Set Down: £7 up to 15 mins, then £25 up to 30 mins.");
        }
      }
      if (id === "PARK_RIDE") {
        warnings.unshift("Free drop-off + shuttle to terminal.");
      }
      if (id === "DROPOFF_ZONE" && isExempt) {
        infoNotes.push("♿ Accessibility priority: Blue Badge / exempt vehicles can use the forecourt.");
        infoNotes.push("ℹ️ Blue Badge holders are exempt from standard drop-off time limits at this terminal. Display badge clearly.");
      }
      return {
        ...raw,
        id,
        label: optionLabel(id),
        tagline: optionTagline(id),
        cost: Number.isFinite(Number(raw.cost)) ? Number(raw.cost) : null,
        warnings,
        infoNotes,
        isExempt: id === "DROPOFF_ZONE" && isExempt
      };
    };

    const dropoff = baseOption("DROPOFF_ZONE");
    const park = baseOption("PARK_RIDE");
    const shortStay = baseOption("SHORT_STAY");
    // Stansted Short Stay pricing bands
    if (shortStay) {
      const mins = numMinutes;
      let price = null;
      if (mins <= 30) price = 10;
      else if (mins <= 60) price = 18;
      else if (mins <= 120) price = 22;
      else if (mins <= 240) price = 35;
      else if (mins <= 1440) price = 35 + Math.ceil((mins - 240) / 60) * 10; // simple extrapolation per hour
      else {
        const extraDays = Math.ceil((mins - 1440) / 1440);
        price = 35 + extraDays * 50;
      }
      shortStay.cost = price;
    }

    // Stansted Mid Stay (Park & Ride) pricing bands
    if (park) {
      const mins = numMinutes;
      let price = 0;
      if (mins <= 60) {
        price = 0;
      } else if (mins <= 120) {
        price = 5;
      } else if (mins <= 1440) {
        price = 45; // Mid Stay turn-up day rate
      } else {
        const extraDays = Math.ceil((mins - 1440) / 1440);
        price = 45 + extraDays * 45;
      }
      park.cost = price;
      park.warnings = [];
      park.infoNotes = ["Free up to 60 mins; £5 for the second hour; shuttle 10–12 mins; £40 re-entry within 1 hour."];
    }

    const medalDecision = () => {
      let copy = "Cheapest option overall.";

      const freeAvailable = (park?.cost === 0) || dropoff?.isExempt;

    if (dropoff?.isExempt) {
        const cheapestOther =
          [park, shortStay]
            .filter(Boolean)
            .sort((a, b) => (a.cost ?? Number.POSITIVE_INFINITY) - (b.cost ?? Number.POSITIVE_INFINITY))[0] || park || shortStay || null;
        const remaining =
          [park, shortStay].filter((o) => o && o.id !== cheapestOther?.id)[0] || (!cheapestOther && park) || (!cheapestOther && shortStay) || dropoff;
        copy = "Blue Badge exemption active — forecourt kept as Gold.";
        return {
          gold: dropoff,
          silver: cheapestOther || dropoff,
          bronze: remaining || dropoff,
          copy,
          freeAvailable
        };
      }

      // A) ≤ 15 minutes → Express Set Down
      if (numMinutes > 0 && numMinutes <= 15) {
        copy = "Fastest option for very quick drop-offs; pay online by midnight next day to avoid PCN.";
        return {
          gold: dropoff || shortStay || park,
          silver: shortStay || dropoff || park,
          bronze: park || shortStay || dropoff,
          copy,
          freeAvailable
        };
      }

      // B) 16–30 minutes → Short Stay
      if (numMinutes > 15 && numMinutes <= 30) {
        copy = "Express Set Down jumps to £25 after 15 minutes; Short Stay is cheaper and walkable.";
        return {
          gold: shortStay || park || dropoff,
          silver: park || dropoff || shortStay,
          bronze: dropoff || park || shortStay,
          copy,
          freeAvailable
        };
      }

      // C) 31–60 minutes → Mid Stay (FREE)
      if (numMinutes > 30 && numMinutes <= 60) {
        copy = "Mid Stay offers 1 hour free for pick-up or drop-off (shuttle included).";
        return {
          gold: park || shortStay || dropoff,
          silver: shortStay || dropoff || park,
          bronze: dropoff || shortStay || park,
          copy,
          freeAvailable
        };
      }

      // D) 61–120 minutes → Mid Stay (£5)
      if (numMinutes > 60 && numMinutes <= 120) {
        copy = "Mid Stay £5 second hour beats Short Stay for longer waits.";
        return {
          gold: park || shortStay || dropoff,
          silver: shortStay || park || dropoff,
          bronze: dropoff || shortStay || park,
          copy,
          freeAvailable
        };
      }

      // E) > 120 minutes → Mid Stay day rate
      if ((shortStay?.cost ?? Number.POSITIVE_INFINITY) <= (park?.cost ?? Number.POSITIVE_INFINITY)) {
        copy = "Short Stay is cheaper than Mid Stay here and avoids the shuttle.";
        return {
          gold: shortStay || park || dropoff,
          silver: park || dropoff || shortStay,
          bronze: dropoff || park || shortStay,
          copy,
          freeAvailable
        };
      }
      copy = "Mid Stay day rate beats Short Stay for longer waits.";
      return {
        gold: park || shortStay || dropoff,
        silver: shortStay || park || dropoff,
        bronze: dropoff || shortStay || park,
        copy,
        freeAvailable
      };

      // 120+: short gold, park silver, drop-off bronze
      copy = "Short Stay wins for longer stays; Long Stay is next best.";
      return {
        gold: shortStay || park || dropoff,
        silver: park || shortStay || dropoff,
        bronze: dropoff || shortStay || park,
        copy,
        freeAvailable
      };
    };

    const { gold, silver, bronze, copy: decisionCopy } = medalDecision();
    const recommendedOpt = gold || dropoff || shortStay || park;
    const recommendedId = recommendedOpt?.id || dropoff?.id;
    const medalOrder = [
      { medal: "GOLD", opt: gold },
      { medal: "SILVER", opt: silver },
      { medal: "BRONZE", opt: bronze }
    ];

    const displayTerminal = "Stansted";

    const minsLabel = Number.isFinite(numMinutes) && numMinutes > 0 ? `${numMinutes} mins` : "your time";
    const headerLine =
      gold
        ? (() => {
            if (gold.id === "PARK_RIDE") return "Mid Stay";
            if (gold.id === "SHORT_STAY") return "Short Stay parking";
            if (gold.id === "DROPOFF_ZONE") return "Express Set Down (drop off)";
            return gold.label || "Recommendation";
          })()
        : "We compared drop-off, Short Stay, and Mid Stay";
    const headlineLabel = recommendedOpt?.label || headerLine;
    const headlineCost =
      recommendedOpt && Number.isFinite(Number(recommendedOpt.cost)) ? Number(recommendedOpt.cost) : null;

    const freeOptions = [];
    if (park?.cost === 0) freeOptions.push(park.label || "Park & Ride");
    if (dropoff?.isExempt || dropoff?.cost === 0) freeOptions.push(dropoff?.label || "Terminal drop-off");
    const goldIsFree = gold && (gold.cost === 0 || gold.isExempt);
    const freeCallout =
      freeOptions.length === 0
        ? "❌ No free option available right now"
        : goldIsFree
        ? "🆓 Free option available — recommended"
        : `🆓 Free option available (${freeOptions[0]})`;

    const heroColors = (() => {
      if (gold?.id === "PARK_RIDE") {
        return {
          bg: "linear-gradient(135deg, #f9e7b4 0%, #f4d87a 100%)",
          text: "#0f172a",
          border: "1px solid #e5e7eb",
          shadow: "0 6px 16px rgba(15, 23, 42, 0.08)"
        };
      }
      if (gold?.id === "SHORT_STAY") {
        return {
          bg: "linear-gradient(135deg, #e5e7eb 0%, #cbd5e1 100%)",
          text: "#0f172a",
          border: "1px solid #d1d5db",
          shadow: "0 6px 16px rgba(15, 23, 42, 0.08)"
        };
      }
      return {
        bg: "radial-gradient(circle at 20% 20%, #1d4ed8 0%, #0f172a 40%, #0b132b 100%)",
        text: "#fff",
        border: "none",
        shadow: "0 10px 25px rgba(0,0,0,0.12)"
      };
    })();

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          color: heroColors.text,
          background: heroColors.bg,
          border: heroColors.border,
          boxShadow: heroColors.shadow
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
          Stansted Drop-off (2026)
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
          {(() => {
            if (recommendedId === "PARK_RIDE") return "Recommendation: Mid Stay";
            if (recommendedId === "SHORT_STAY") return "Recommendation: Short Stay parking";
            return "Recommendation: Express Set Down";
          })()}
        </div>
        <div style={{ fontSize: 14, marginTop: 6, opacity: 0.9 }}>
          Cost for your stay ({headlineLabel}): <strong>{money(headlineCost)}</strong>
        </div>
        <div style={{ fontSize: 13, marginTop: 6, opacity: 0.9 }}>
          {(() => {
            if (recommendedId === "PARK_RIDE") {
              if (numMinutes <= 60) return `Why: For ${minsLabel}, Mid Stay is free up to 60 minutes; includes shuttle 10–12 mins.`;
              if (numMinutes <= 120) return `Why: For ${minsLabel}, Mid Stay is £5 and cheaper than Short Stay; includes shuttle 10–12 mins.`;
              return `Why: For ${minsLabel}, Mid Stay day rate beats Short Stay; includes shuttle 10–12 mins.`;
            }
            if (recommendedId === "SHORT_STAY") {
              return `Why: For ${minsLabel}, Short Stay is cheaper than the forecourt after 15 mins and avoids the £25 step.`;
            }
            return `Why: For ${minsLabel}, Express Set Down is the fastest option; pay online by midnight next day to avoid PCN.`;
          })()}
        </div>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 10
          }}
        >
          {medalOrder.map(({ medal, opt }) => {
            const paletteFor = palette[medal];
            return (
              <div
                key={medal}
                style={{
                  border: `1px solid ${paletteFor.border}`,
                  borderRadius: 14,
                  padding: 14,
                  background: paletteFor.bg,
                  color: paletteFor.text,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minHeight: 140
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.5, color: "#fff" }}>
                  {paletteFor.emoji} {medal}
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>{opt?.label || "Awaiting option"}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>
                  {opt?.cost != null ? money(opt.cost) : "—"}
                </div>
                <div style={{ fontSize: 12, color: "#e5e7eb", lineHeight: 1.4 }}>{opt?.tagline || "Compare safety, speed, and price."}</div>
                {Array.isArray(opt?.warnings) && opt.warnings[0] && (
                  <div style={{ fontSize: 12, color: paletteFor.text }}>{`⚠️ ${opt.warnings[0]}`}</div>
                )}
                {Array.isArray(opt?.infoNotes) &&
                  opt.infoNotes.map((n, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: "#cbd5e1" }}>
                      {n}
                    </div>
                  ))}
                <div style={{ marginTop: "auto" }}>
                  <button
                    type="button"
                    onClick={() => {
                      const map = {
                        DROPOFF_ZONE: "dropoff_zone",
                        SHORT_STAY: "short_stay",
                        PARK_RIDE: "park_ride"
                      };
                      const key = map[opt?.id];
                      if (!key) return;
                      const el = document.getElementById(`collapse-${key}`);
                      if (el) {
                        el.open = true;
                        requestAnimationFrame(() => {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      }
                    }}
                    style={{
                      marginTop: 8,
                      background: "transparent",
                      border: "1px solid rgba(255,255,255,0.2)",
                      color: "#fff",
                      padding: "6px 10px",
                      borderRadius: 10,
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 12
                    }}
                  >
                    View details ▸
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 12 }}>
          {result?.taxi_alternative && (
            <div style={{ padding: 10, borderRadius: 10, border: "1px solid #e5e7eb", background: "#f8fafc", color: "#0f172a" }}>
              <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 12 }}>💡 {result.taxi_alternative.title}</div>
              <div style={{ fontSize: 12, color: "#374151" }}>
                {result.taxi_alternative.message}{" "}
                <a
                  href={result.taxi_alternative.cta || "/heathrow-fare-compare"}
                  style={{ fontWeight: 700, color: "#0f172a", textDecoration: "underline" }}
                >
                  Compare taxi fares →
                </a>
              </div>
            </div>
          )}

        <div style={{ fontSize: 12, color: heroColors.text, opacity: heroColors.text === "#fff" ? 0.85 : 0.9, marginTop: 6 }}>
            Terminal: <strong>{displayTerminal}</strong> • Type: <strong>{result.journey?.journey_type}</strong> • Planned time:{" "}
            <strong>{result.journey?.planned_minutes} mins</strong>
            {result.journey?.overheight ? " • Overheight" : ""}
            {result.journey?.blue_badge ? " • Blue Badge" : ""}
          </div>
        </div>
      </div>
    );
  };

  const renderOptionCard = (opt) => {
    const isBest = result?.recommendation?.best === opt.id;

    const linkBlocks = {
      DROPOFF_ZONE: [
        { label: "Stansted drop-off info", href: "https://www.stanstedairport.com/parking/drop-off/" },
        { label: "Pay or appeal a PCN", href: "https://www.ncp.co.uk/help-centre/pcn-payment-and-appeals/" },
        { label: "Blue Badge registration", href: "https://www.stanstedairport.com/accessibility/" },
        { label: "Customer services: 0808 169 7030", href: "tel:08081697030" }
      ],
      PARK_RIDE: [
        { label: "Long Stay / Park & Ride info", href: "https://www.gatwickairport.com/parking/parking-long-stay.html" }
      ],
      SHORT_STAY: [
        { label: "Short Stay parking prices", href: "https://www.gatwickairport.com/parking/roll-up-car-park-prices.html" }
      ]
    };

    return (
      <div
        key={opt.id}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
          background: isBest ? "#ecfdf5" : "#fff"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontWeight: 800 }}>
            {opt.title}
            {isBest && badge("Recommended", "ok")}
          </div>
          <div style={{ fontWeight: 800 }}>{money(opt.cost)}</div>
        </div>

        {opt.best_for?.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 13, color: "#374151" }}>
            Best for:{" "}
            <span style={{ color: "#111827" }}>
              {opt.best_for.includes("free_dropoff") ? "free / cheapest, " : ""}
              {opt.best_for.includes("pickup_best") ? "pick-ups, " : ""}
              {opt.best_for.includes("fastest") ? "fastest, " : ""}
              {opt.best_for.includes("waiting") ? "waiting, " : ""}
              {opt.best_for.includes("meet_and_greet_style") ? "meet & greet style, " : ""}
            </span>
          </div>
        )}

        {opt.id === "DROPOFF_ZONE" && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Express Set Down (drop off)</div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Best for: Very quick drop-offs only</div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Location: Express Set Down (forecourt)</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Charges</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Up to 15 minutes: £7</li>
              <li>Over 15 minutes: £25</li>
              <li>Maximum stay: 30 minutes</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>Important rules</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Payment is post-visit only</li>
              <li>Pay online or by phone by midnight the following day</li>
              <li>Vehicles must not be left unattended</li>
              <li>Re-entering within 30 minutes costs £20</li>
              <li>Unauthorised vehicles may be towed</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>PCN risk</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Failure to pay results in a £100 Parking Charge Notice</li>
              <li>Reduced to £60 if paid within 14 days</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>How to pay</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Pay online: <a href="https://pay.stanstedairport.com/" target="_blank" rel="noreferrer">pay.stanstedairport.com</a></li>
              <li>Phone payment: <a href="tel:03450175046">0345 017 5046</a></li>
            </ul>
          </div>
        )}

        {opt.id === "SHORT_STAY" && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Short Stay parking (Orange, Green &amp; Blue)</div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Best for: Pick-ups, waiting, or entering the terminal</div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Walkable to terminal</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Charges</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Up to 30 minutes: £10</li>
              <li>Up to 1 hour: £18</li>
              <li>Up to 2 hours: £22</li>
              <li>Up to 4 hours: £35</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>Notes</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Ideal if you need to meet passengers inside</li>
              <li>Safer than Express Set Down if you expect delays</li>
              <li>Standard parking rules apply</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>Short Stay locations</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Short Stay Blue — Postcode: CM24 1PP</li>
              <li>Short Stay Green — Postcode: CM24 1QW</li>
              <li>Short Stay Orange — Postcode: CM24 1QW</li>
              <li>(All signed from the M11)</li>
            </ul>
          </div>
        )}

        {opt.id === "PARK_RIDE" && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Mid Stay</div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Best for: Free pick-up or drop-off with time flexibility</div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>Includes shuttle transfer</div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Charges</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Up to 60 minutes: FREE</li>
              <li>Second hour: £5</li>
              <li>After 2 hours: Day rate applies</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>Important</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Re-entering within 1 hour costs £40</li>
              <li>Shuttle buses run frequently</li>
              <li>Recommended if you’re unsure on timing</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>Location</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Mid Stay Car Park</li>
              <li>Postcode: CM24 1PZ</li>
              <li>Signed from the M11</li>
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderOptionSection = (opt, title, defaultOpen = false) => {
    if (!opt) return null;
    return (
      <details id={`collapse-${opt.id.toLowerCase().replace(/_/g, "_")}`} className="train-collapsible" open={defaultOpen} style={{ marginTop: 12 }}>
        <summary className="train-summary-bar">{title}</summary>
        <div className="train-collapsible-body">{renderOptionCard(opt)}</div>
      </details>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Stansted Drop-off Calculator (2026): Compare Fees &amp; Time</title>

        <meta
          name="description"
          content="Stansted drop-off calculator with live timing alerts. See exactly when the £7 fee jumps to £25 and avoid £100 PCNs using official 2026 rates."
        />

        <meta
          name="keywords"
          content="Stansted drop off charge, Stansted drop off fee, Stansted free drop off, Stansted short stay prices, Stansted pickup parking cost, Stansted terminal drop off rules, Stansted drop off penalty"
        />

        <link rel="canonical" href="https://www.cheapcabgrab.com/stansted-dropoff-calculator" />

        <meta property="og:title" content="Stansted Drop-off Calculator (2026): Compare Fees &amp; Time" />
        <meta
          property="og:description"
          content="Stansted drop-off calculator with live timing alerts. See exactly when the £7 fee jumps to £25 and avoid £100 PCNs using official 2026 rates."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/stansted-dropoff-calculator" />

        {/* FAQ Rich Snippet */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How much is the Stansted terminal drop-off charge?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "The Stansted drop-off charge is £7 as of 2026. Payment is due by midnight the same day (online/phone/autopay). No pay machines at the terminal."
                }
              },
              {
                "@type": "Question",
                name: "Is there a free drop-off option at Stansted?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Use Stansted Long Stay car parks for free drop-off up to the posted time limit (check signage) with a shuttle to the terminal."
                }
              },
              {
                "@type": "Question",
                name: "What is the Stansted drop-off penalty?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Forecourt drop-off incurs a daily charge. Unpaid/overstay penalties can be issued (check current Stansted enforcement rules)."
                }
              },
              {
                "@type": "Question",
                name: "Should I use Short Stay parking for pick-ups at Stansted?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. For pick-ups or waiting, use Short Stay car parks. Forecourt drop-off is not suitable for waiting."
                }
              },
              {
                "@type": "Question",
                name: "How much is Stansted Short Stay parking for a few hours?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Short Stay prices vary by duration. This calculator compares your planned time to Short Stay tariffs and surfaces the safest option for your stay."
                }
              },
              {
                "@type": "Question",
                name: "Do taxis and private-hire vehicles pay the Stansted drop-off charge?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. Taxis and private-hire vehicles pay the standard forecourt charge unless a discount/exemption applies."
                }
              },
              {
                "@type": "Question",
                name: "Can Blue Badge holders get a discount for Stansted drop-off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Blue Badge concessions can apply at Stansted (check current rules). Always register/confirm via official channels."
                }
              },
              {
                "@type": "Question",
                name: "What does this Stansted drop-off calculator check?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "It compares the Stansted forecourt charge against Long Stay (free option) and Short Stay based on your planned time, and highlights payment deadlines and exemptions."
                }
              }
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Stansted Drop-off Charge & Short Stay Calculator",
            url: "https://www.cheapcabgrab.com/stansted-dropoff-calculator",
            description:
              "Calculate Stansted terminal drop-off charge vs Long Stay (free drop-off) vs Short Stay parking costs, including rules and warnings.",
            inLanguage: "en-GB",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is Stansted drop-off free anywhere?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Free drop-off is available at Stansted Long Stay car parks within posted time limits, with a shuttle to the terminal. This tool compares your options by duration."
                }
              },
              {
                "@type": "Question",
                name: "What happens if I stay longer at Stansted drop-off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Overstaying or not paying the Stansted forecourt charge can lead to enforcement penalties. This tool highlights the risk based on your planned time."
                }
              },
              {
                "@type": "Question",
                name: "Where should I pick up passengers at Stansted?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Pick-ups are usually made from Short Stay or Long Stay, not the terminal forecourt. This tool recommends the best option for pick-ups."
                }
              }
            ]
          })}
        </script>

        {/* Software Application schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Airport Drop-off Price Engine",
            applicationCategory: "TravelApplication",
            operatingSystem: "All",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP"
            },
            description:
              "Dynamic calculator for UK airport drop-off fees. Re-ranks parking options (Gold/Silver/Bronze) in real-time based on minutes stayed."
          })}
        </script>
      </Helmet>

      <LiveStrip variant="stansted" />

      <div className="parking-header">
        <h1>🚗 Stansted Drop-off & Short Stay Calculator</h1>
        <p>
          Compare <strong>Stansted forecourt drop-off charge</strong> vs <strong>Long Stay (free option)</strong> vs{" "}
          <strong>Short Stay</strong>. Includes Blue Badge handling, penalty risk, and shuttle info.
        </p>
        <p style={{ fontSize: 14, color: "#374151" }}>
          Try quick drop off version <a href="/stansted-dropoff">Use the free drop-off guide.</a>
        </p>
      </div>

      {/* FORM */}
      <div className="parking-form">
        <div className="minutes-card" style={{ marginTop: 4 }}>
          <div className="minutes-label-row">
            <label style={{ margin: 0 }} htmlFor="planned_minutes">Duration of stay (mins)</label>
            <span className="minutes-chip">Time</span>
          </div>
          <div className="minutes-input-row">
            <input
              type="number"
              className="parking-input"
              min="1"
              max="10080"
              step="1"
              id="planned_minutes"
              value={form.planned_minutes}
              onChange={(e) =>
                setForm({
                  ...form,
                  planned_minutes: e.target.value === "" ? "" : Math.min(10080, Math.max(1, Number(e.target.value)))
                })
              }
              onBlur={() => {
                setForm({ ...form, planned_minutes: clampMinutes(form.planned_minutes) });
              }}
            />
            <div className="minutes-nudge">
              <button
                type="button"
                onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) - 1) })}
              >
                −
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) + 1) })}
              >
                +
              </button>
            </div>
          </div>
        </div>

        <button onClick={submit} className="parking-btn">
          {loading ? "Calculating…" : "Calculate best option"}
        </button>

        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
      </div>

      {/* RESULTS */}
      {result && (
        <div className="parking-results">
          {renderMedalHero()}

          {(() => {
            const map = {};
            (Array.isArray(result.options) ? result.options : []).forEach((o) => {
              map[o.id] = o;
            });
            const drop = map["DROPOFF_ZONE"] || null;
            const park = map["PARK_RIDE"] || null;
            const shortStay = map["SHORT_STAY"] || null;

            if (drop) {
              drop.notes = [];
              drop.warnings = [];
            }

            if (park) {
              park.best_for = ["free_dropoff", "waiting"];
              park.warnings = [];
              park.notes = [
                "Mid Stay free allowance up to 60 minutes; £5 for second hour; day rate applies after.",
                "Includes shuttle transfer to terminal (10–12 mins).",
                "Re-entering within 1 hour costs £40."
              ];
            }

            return (
              <>
                {renderOptionSection(drop, "Terminal drop-off", false)}
                {renderOptionSection(shortStay, "Short Stay parking", false)}
                {renderOptionSection(park, "Mid Stay", false)}
              </>
            );
          })()}

          {Array.isArray(result.notices) && result.notices.length > 0 && (
            <div style={{ marginTop: 12, background: "#f9fafb", border: "1px solid #e5e7eb", padding: 12, borderRadius: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>ℹ️ Notes</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                {result.notices.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}

         <details className="train-collapsible">
           <summary className="train-summary-bar">ℹ️ What this tool checks (Stansted)</summary>
           <div className="train-collapsible-body">
             <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Express Set Down charges.</li>
                <li>Short Stay parking price bands.</li>
                <li>Mid Stay free allowance.</li>
                <li>PCN risk and payment deadlines.</li>
                <li>Re-entry penalties.</li>
                <li>Shuttle time considerations.</li>
                <li>Highlights the cheapest and safest option for your stay duration.</li>
             </ul>
           </div>
         </details>

         <details className="train-collapsible" style={{ marginTop: 12 }}>
           <summary className="train-summary-bar">🆘 Need help with Stansted drop-off or parking?</summary>
           <div className="train-collapsible-body">
             <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>📞 APCOA Customer Services: <a href="tel:03450175046">0345 017 5046</a></li>
                <li>💬 Live webchat: <a href="https://pay.stanstedairport.com/" target="_blank" rel="noreferrer">pay.stanstedairport.com</a></li>
                <li>🧾 Appeal a Parking Charge (APCOA): <a href="https://payments.apcoa.co.uk/" target="_blank" rel="noreferrer">payments.apcoa.co.uk</a></li>
             </ul>
           </div>
         </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">✅ Why this Stansted calculator is reliable</summary>
            <div className="train-collapsible-body">
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
                <p style={{ marginTop: 0, marginBottom: 6, fontSize: 14, color: "#374151" }}>
                  This calculator uses published rules and pricing from London Stansted Airport and APCOA to compare:
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, fontSize: 13, color: "#374151" }}>
                  <li>Express Set Down forecourt charges.</li>
                  <li>Short Stay and Mid Stay pricing.</li>
                  <li>Free eligibility based on duration and vehicle type.</li>
                  <li>PCN risk thresholds and payment deadlines.</li>
                </ul>
                <div style={{ marginTop: 8, fontSize: 13, color: "#374151", fontWeight: 700 }}>What makes it useful</div>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, fontSize: 13, color: "#374151" }}>
                  <li>Updated to reflect current Stansted pricing bands and limits.</li>
                  <li>Flags when a “free” option disappears as stay time increases.</li>
                  <li>Explains why one option becomes Gold / Silver / Bronze.</li>
                  <li>Designed to surface the cheapest safe option, not just the lowest headline price.</li>
                </ul>
                <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                  Rules and prices can change. Always check official Stansted signage and payment pages before travel.
                </div>
              </div>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">💡 Why Stansted Mid Stay can be free</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Stansted offers a free 60-minute allowance at Mid Stay to reduce forecourt congestion.</li>
                <li>This makes Mid Stay the safest free option for delayed flights or longer waits.</li>
                <li>You trade speed for zero cost and no PCN risk.</li>
              </ul>
            </div>
          </details>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
            <strong>🔗 Official Stansted references (used by this tool)</strong>
            <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
              <li>Drop-off charges &amp; payment: <a href="https://www.stanstedairport.com/parking/drop-off/" target="_blank" rel="noreferrer">stanstedairport.com/parking/drop-off/</a></li>
              <li>Parking prices: <a href="https://www.stanstedairport.com/parking/" target="_blank" rel="noreferrer">stanstedairport.com/parking/</a></li>
              <li>Long Stay parking: <a href="https://www.stanstedairport.com/parking/long-stay/" target="_blank" rel="noreferrer">stanstedairport.com/parking/long-stay/</a></li>
            </ul>
            <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
              Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 40 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
